import { useQuery } from '@tanstack/react-query';
import { flows, templates } from '@repletoryreach/sdk-api';
import type { ChecklistItem, Guide } from '../types';
import { isKnowledgeAssistant } from '../../../pages/knowledge/assistant-template';
import { builders } from './helpers';

// The Automation guide (8 October 2026): Templates, Flows and the Knowledge base. Words under guide.automation.*.

const { link, howLink, step, how, section, howTo } = builders('automation');
const L = {
  templates: link('templates', '/templates'),
  newTemplate: link('newTemplate', '/templates/new'),
  flows: link('flows', '/flows'),
  allFlows: link('allFlows', '/flows/all'),
  ideas: link('ideas', '/flows/ideas'),
  knowledge: link('knowledge', '/knowledge'),
  persona: link('persona', '/settings/assistant'),
  channels: link('channels', '/channels'),
  numbers: link('numbers', '/numbers'),
  broadcasts: link('broadcasts', '/broadcasts'),
  billing: link('billing', '/billing/usage'),
  bookings: link('bookings', '/bookings/services'),
  tickets: link('tickets', '/tickets/settings'),
  languages: link('languages', '/settings/assistant'),
  howTemplate: howLink('howTemplate', 'template'),
  howFlow: howLink('howFlow', 'flow'),
  howLooseEnds: howLink('howLooseEnds', 'looseEnds'),
  howLanguage: howLink('howLanguage', 'language'),
  howKnowledge: howLink('howKnowledge', 'knowledge')
};

function useAutomationChecklist(): { items: ChecklistItem[]; loading: boolean } {
  const tpl = useQuery({ queryKey: ['templates'], queryFn: () => templates.list(), staleTime: 60_000 });
  const flowList = useQuery({ queryKey: ['flows', 'guide'], queryFn: () => flows.listFlows(), staleTime: 60_000 });
  const docs = useQuery({ queryKey: ['kb-documents'], queryFn: () => flows.listKbDocuments({ limit: 200 }), staleTime: 60_000 });
  const all = flowList.data?.data ?? [];
  const item = (id: string, to: string, done: boolean | undefined, optional = false): ChecklistItem => ({
    id, to, done, optional, titleKey: `guide.automation.check.${id}.title`, hintKey: `guide.automation.check.${id}.hint`
  });
  const items = [
    item('template', '/templates', tpl.data ? (tpl.data.data ?? []).some((x) => x.status === 'approved') : undefined),
    item('persona', '/settings/assistant', undefined),
    item('welcome', '/flows', flowList.data ? all.some((f) => f.is_active && JSON.stringify(f.definition ?? {}).includes('"first_contact"')) : undefined),
    item('documents', '/knowledge', docs.data ? (docs.data.data ?? []).length > 0 : undefined),
    item('answers', '/knowledge?tab=chatbot', flowList.data ? all.some((f) => f.is_active && isKnowledgeAssistant(f.definition)) : undefined),
    item('tested', '/flows', undefined),
    item('flow', '/flows', flowList.data ? all.filter((f) => f.is_active).length > 1 : undefined, true)
  ];
  return { items, loading: [tpl, flowList, docs].some((q) => q.isPending) };
}

const K = '/knowledge?tab=';

export const AUTOMATION_GUIDE: Guide = {
  id: 'automation',
  version: 1,
  titleKey: 'guide.automation.title',
  introKey: 'guide.automation.intro',
  routes: ['/templates', '/flows', '/knowledge'],
  tour: [
    step('welcome', undefined, undefined, [L.numbers]),
    step('templates', '/templates', 'templates-list'),
    step('newTemplate', '/templates', 'templates-new', [L.howTemplate]),
    step('flowTabs', '/flows', 'flows-tabs'),
    step('entries', '/flows?tab=entries', 'flows-entries'),
    step('channels', '/flows?tab=channels', 'flows-channels', [L.channels]),
    step('questions', '/flows?tab=questions', 'flows-questions'),
    step('coach', '/flows?tab=coach', 'flows-coach'),
    step('persona', '/flows', 'flows-persona'),
    step('sidebar', '/flows', 'flows-sidebar'),
    step('newFlow', '/flows', 'flows-new', [L.howFlow]),
    step('knowledgeTabs', '/knowledge', 'knowledge-tabs'),
    step('knowledgeAdd', `${K}documents`, 'knowledge-add', [L.howKnowledge]),
    step('knowledgeTry', `${K}try`, 'knowledge-try'),
    step('knowledgeConflicts', `${K}conflicts`, 'knowledge-conflicts'),
    step('knowledgeChatbot', `${K}chatbot`, 'knowledge-chatbot'),
    step('help', '/knowledge', 'automation-help')
  ],
  howTos: [
    howTo('template', [
      how('template', 'start', '/templates/new', 'template-wizard'),
      how('template', 'basics', '/templates/new', 'template-wizard'),
      how('template', 'content', '/templates/new', 'template-wizard'),
      how('template', 'review', '/templates/new', 'template-wizard', { links: [L.templates] })
    ]),
    howTo('flow', [
      how('flow', 'open', '/flows', 'flows-sidebar'),
      how('flow', 'identity', undefined, 'builder-identity'),
      how('flow', 'add', undefined, 'builder-add', { links: [L.bookings, L.tickets] }),
      how('flow', 'canvas', undefined, undefined),
      how('flow', 'ai', undefined, 'builder-ai'),
      how('flow', 'test', undefined, 'builder-test'),
      how('flow', 'save', undefined, 'builder-save')
    ]),
    // Suggest flows with AI (30 October 2026).
    howTo('ideas', [
      how('ideas', 'open', '/flows/ideas', 'ideas-start'),
      how('ideas', 'settings', '/flows/ideas', 'ideas-start'),
      how('ideas', 'ai', '/flows/ideas', 'ideas-ai'),
      how('ideas', 'pick', undefined, undefined)
    ]),
    // Loose ends (2 November 2026): steps in switched-on flows that lead nowhere, reviewed in the Flows page's drawer.
    howTo('looseEnds', [
      how('looseEnds', 'review', '/flows?loose_ends=1', 'flows-loose-ends'),
      how('looseEnds', 'fix', '/flows?loose_ends=1', 'flows-loose-ends'),
      how('looseEnds', 'prevent', undefined, undefined, { links: [L.allFlows] })
    ]),
    // Change language (3 November 2026): one step instead of a language menu built from an Ask and an Update contact.
    howTo('language', [
      how('language', 'languages', '/settings/assistant', 'assistant-languages', { links: [L.languages] }),
      how('language', 'open', '/flows', 'flows-sidebar'),
      how('language', 'add', undefined, 'builder-add'),
      how('language', 'link', undefined, undefined)
    ]),
    howTo('knowledge', [
      how('knowledge', 'add', `${K}documents`, 'knowledge-add'),
      how('knowledge', 'websites', `${K}websites`, 'knowledge-websites'),
      how('knowledge', 'try', `${K}try`, 'knowledge-try'),
      how('knowledge', 'chatbot', `${K}chatbot`, 'knowledge-chatbot', { links: [L.persona] })
    ])
  ],
  sections: [
    section('overview', 3, 1, [L.templates, L.flows, L.knowledge]),
    section('templates', 3, 2, [L.howTemplate, L.newTemplate]),
    section('categories', 2, 1, [L.billing]),
    section('flows', 3, 1, [L.flows, L.allFlows]),
    section('entries', 2, 1, [L.flows]),
    section('builder', 4, 2, [L.howFlow]),
    section('ideas', 3, 1, [L.ideas]),
    section('looseEnds', 2, 1, [L.howLooseEnds, L.flows]),
    section('steps', 3, 1, [L.bookings, L.tickets, L.howLanguage]),
    section('persona', 2, 0, [L.persona]),
    section('knowledge', 3, 1, [L.howKnowledge, L.knowledge]),
    section('accuracy', 2, 1, [L.knowledge]),
    section('questions', 2, 0, [L.flows]),
    section('coach', 2, 1, [L.flows]),
    section('costs', 2, 1, [L.billing]),
    section('trouble', 5, 0, [L.templates, L.flows, L.knowledge])
  ],
  useChecklist: useAutomationChecklist
};
